/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  ExperimentDto,
  ExperimentSummaryDto,
  FlowRunDto,
  PromotedAssetDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary A flow's experiments, newest first.
 */
export const listFlowExperiments = (id: string, flowId: string, signal?: AbortSignal) => {
  return customInstance<ExperimentSummaryDto[]>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}/experiments`,
    method: 'GET',
    signal,
  });
};

export const getListFlowExperimentsQueryKey = (id: string, flowId: string) => {
  return [`/api/v1/workspaces/${id}/flows/${flowId}/experiments`] as const;
};

export const getListFlowExperimentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listFlowExperiments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListFlowExperimentsQueryKey(id, flowId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFlowExperiments>>> = ({
    signal,
  }) => listFlowExperiments(id, flowId, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && flowId !== null && flowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListFlowExperimentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listFlowExperiments>>
>;
export type ListFlowExperimentsQueryError = ErrorType<ApiErrorDto>;

export function useListFlowExperiments<
  TData = Awaited<ReturnType<typeof listFlowExperiments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowExperiments>>,
          TError,
          Awaited<ReturnType<typeof listFlowExperiments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowExperiments<
  TData = Awaited<ReturnType<typeof listFlowExperiments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowExperiments>>,
          TError,
          Awaited<ReturnType<typeof listFlowExperiments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowExperiments<
  TData = Awaited<ReturnType<typeof listFlowExperiments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A flow's experiments, newest first.
 */

export function useListFlowExperiments<
  TData = Awaited<ReturnType<typeof listFlowExperiments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowExperiments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFlowExperimentsQueryOptions(id, flowId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary The Results page: variants, their numbers and the winner.
 */
export const getExperiment = (id: string, experimentId: string, signal?: AbortSignal) => {
  return customInstance<ExperimentDto>({
    url: `/api/v1/workspaces/${id}/experiments/${experimentId}`,
    method: 'GET',
    signal,
  });
};

export const getGetExperimentQueryKey = (id: string, experimentId: string) => {
  return [`/api/v1/workspaces/${id}/experiments/${experimentId}`] as const;
};

export const getGetExperimentQueryOptions = <
  TData = Awaited<ReturnType<typeof getExperiment>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  experimentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExperimentQueryKey(id, experimentId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getExperiment>>> = ({
    signal,
  }) => getExperiment(id, experimentId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null &&
      id !== undefined &&
      experimentId !== null &&
      experimentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetExperimentQueryResult = NonNullable<
  Awaited<ReturnType<typeof getExperiment>>
>;
export type GetExperimentQueryError = ErrorType<ApiErrorDto>;

export function useGetExperiment<
  TData = Awaited<ReturnType<typeof getExperiment>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  experimentId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExperiment>>,
          TError,
          Awaited<ReturnType<typeof getExperiment>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetExperiment<
  TData = Awaited<ReturnType<typeof getExperiment>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  experimentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExperiment>>,
          TError,
          Awaited<ReturnType<typeof getExperiment>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetExperiment<
  TData = Awaited<ReturnType<typeof getExperiment>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  experimentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The Results page: variants, their numbers and the winner.
 */

export function useGetExperiment<
  TData = Awaited<ReturnType<typeof getExperiment>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  experimentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExperiment>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetExperimentQueryOptions(id, experimentId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save the winner to Media as a draft.
 */
export const promoteWinner = (id: string, experimentId: string, signal?: AbortSignal) => {
  return customInstance<PromotedAssetDto>({
    url: `/api/v1/workspaces/${id}/experiments/${experimentId}/promote`,
    method: 'POST',
    signal,
  });
};

export const getPromoteWinnerMutationKey = () => ['promoteWinner'] as const;

export const getPromoteWinnerMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof promoteWinner>>,
    TError,
    PromoteWinnerMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof promoteWinner>>,
  TError,
  PromoteWinnerMutationVariables,
  TContext
> => {
  const mutationKey = getPromoteWinnerMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof promoteWinner>>,
    PromoteWinnerMutationVariables
  > = (props) => {
    const { id, experimentId } = props ?? {};

    return promoteWinner(id, experimentId);
  };

  return { mutationFn, ...mutationOptions };
};

export type PromoteWinnerMutationResult = NonNullable<
  Awaited<ReturnType<typeof promoteWinner>>
>;

export type PromoteWinnerMutationError = ErrorType<ApiErrorDto>;
export type PromoteWinnerMutationVariables = { id: string; experimentId: string };

/**
 * @summary Save the winner to Media as a draft.
 */
export const usePromoteWinner = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof promoteWinner>>,
      TError,
      PromoteWinnerMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof promoteWinner>>,
  TError,
  PromoteWinnerMutationVariables,
  TContext
> => {
  return useMutation(getPromoteWinnerMutationOptions(options), queryClient);
};
/**
 * @summary Run the flow again with 5 variants like the winner.
 */
export const makeMoreLikeWinner = (
  id: string,
  experimentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<FlowRunDto>({
    url: `/api/v1/workspaces/${id}/experiments/${experimentId}/more`,
    method: 'POST',
    signal,
  });
};

export const getMakeMoreLikeWinnerMutationKey = () => ['makeMoreLikeWinner'] as const;

export const getMakeMoreLikeWinnerMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof makeMoreLikeWinner>>,
    TError,
    MakeMoreLikeWinnerMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof makeMoreLikeWinner>>,
  TError,
  MakeMoreLikeWinnerMutationVariables,
  TContext
> => {
  const mutationKey = getMakeMoreLikeWinnerMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof makeMoreLikeWinner>>,
    MakeMoreLikeWinnerMutationVariables
  > = (props) => {
    const { id, experimentId } = props ?? {};

    return makeMoreLikeWinner(id, experimentId);
  };

  return { mutationFn, ...mutationOptions };
};

export type MakeMoreLikeWinnerMutationResult = NonNullable<
  Awaited<ReturnType<typeof makeMoreLikeWinner>>
>;

export type MakeMoreLikeWinnerMutationError = ErrorType<ApiErrorDto>;
export type MakeMoreLikeWinnerMutationVariables = { id: string; experimentId: string };

/**
 * @summary Run the flow again with 5 variants like the winner.
 */
export const useMakeMoreLikeWinner = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof makeMoreLikeWinner>>,
      TError,
      MakeMoreLikeWinnerMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof makeMoreLikeWinner>>,
  TError,
  MakeMoreLikeWinnerMutationVariables,
  TContext
> => {
  return useMutation(getMakeMoreLikeWinnerMutationOptions(options), queryClient);
};
